import type { Editor } from '@tiptap/react';
import { Chat } from '@ai-sdk/react';
import type { ChatAddToolOutputFunction, ChatRequestOptions, UIMessage } from 'ai';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import {
  type AgentContext,
  buildAgentDocumentContext,
  buildAgentInstructionBodyFields,
  type GenerateDocumentNameVariables,
  serializeUserSources,
} from '@/hooks/llm/prompt-invocation-builders';
import { ENVIRONMENT } from '@/constants';
import { clearSubmittedFeedback } from '@/components/assistant-message-feedback/message-feedback-store';
import { getAgentTransportConfig } from '@flintmint/web-shared/utils/llm-agent-utils';
import {
  createChatTranscript,
  getDisplayText,
  shouldAutoSend,
  type ChatMessage,
} from '@flintmint/web-shared/utils/llm-message-utils';
import { stripHtml } from '@flintmint/core-shared/utils';
import { normalizeClassificationSource, type ClassificationCache } from '@flintmint/core-shared/medical-note';

import { createAgentRunLifecycle, type AgentLifecycleRuntime, type AgentRunLifecycle } from '../run/finalize-run';
import type { PreparedAgentAttempt } from '../run/prepare-run';
import type { AiRefusal } from '../run/agent-run-model';
import { buildChatRequestOptions, getContextParams, handleAgentToolCall } from './agent-tool-call-handlers';
import {
  getAgentRunEditScope,
  getAgentRunEditTarget,
  hasAgentRunClaimedTerminalization,
  startAgentRun,
} from '../run/agent-run-store';
import { markEditorWritesRefused } from '../current-turn-assistant-messages';
import {
  createDocumentEditTarget,
  createTargetedEditTarget,
  getAgentEditScope,
  getTargetedEditRange,
  type AgentEditTarget,
} from '../edit-target';
import { buildPrefetchMessages, prefetchDocumentContent, stripSyntheticPrefetchAssistantMessages } from './prefetch-utils';
import { getEditorHTML, isAiToolkitReady, isEditorAlive } from '@/utils/editor-utils';
import { StreamingChatTransport } from '../streaming/streaming-chat-transport';
import { hasCompletedEditorWriteInCurrentTurn } from '@flintmint/core-shared/utils';
import { getAgentRequestDocumentFields, isNoteDocument, type PersistedDocument } from '../document-utils';
import { isLikelyNetworkError } from '../is-likely-network-error';
import type { PersistedDocumentWriter } from '../review/review-session-types';
import type { DocumentNameDecision } from '@flintmint/core-shared/message-metadata';
import { runPromptMode } from './prompt-mode-client';
import { AiCreditsExhaustedError, AuthDeniedError, fetchAgent } from './agent-fetch';
import { createGenerateTextAction, type GenerateTextContextParams, type GenerateTextSessionParams } from './agent-generate-text';

export type AgentSessionRuntime = AgentLifecycleRuntime & {
  addToolOutput: ChatAddToolOutputFunction<UIMessage>;
  getContext: (params: any) => Promise<any>;
  getGenerateTextContext: (params: GenerateTextContextParams) => Promise<any>;
  streamingEnabled: boolean;
};

export type GenerateDocumentName = (variables: GenerateDocumentNameVariables) => Promise<DocumentNameDecision>;

type AgentSessionRuntimeState = {
  runtime?: AgentSessionRuntime;
  documentFallback?: PersistedDocument;
  persistDocumentPatchFallback?: PersistedDocumentWriter;
};

type AgentSessionReplayFinishEvent = {
  messages?: UIMessage[];
};

type AgentSessionFinishEvent = AgentSessionReplayFinishEvent & {
  message?: UIMessage;
  isAbort: boolean;
  isDisconnect: boolean;
  isError: boolean;
  finishReason?: string;
};

export type AgentSession = {
  requestId: string;
  chat: Chat<UIMessage>;
  setRuntime(runtime: AgentSessionRuntime): void;
  markMounted(): void;
  markUnmounted(): void;
  isHookMounted(): boolean;
  updateChatTranscript(messages: UIMessage[]): ChatMessage[];

  send(text: string, options?: { editTarget?: AgentEditTarget }): Promise<void>;
  generateText(params: GenerateTextSessionParams): Promise<void>;
  generateDocumentName: GenerateDocumentName;
  retry(): Promise<void>;
  cancel(): Promise<void>;
};

export interface SendMessageCommand {
  requestId: string;
  text: string;
  options?: { editTarget?: AgentEditTarget };
}

let agentAccessTokenGetter: (() => Promise<string | null>) | null = null;
let cachedAgentAccessToken: { token: string; exp: number } | null = null;
const agentSessions = new Map<string, AgentSession>();

export function setAgentAccessTokenGetter(getter: (() => Promise<string | null>) | null): void {
  agentAccessTokenGetter = getter;
  // Unregistering (sign-out, or test isolation) also clears the cached token so a freshly
  // registered getter isn't held back by a previous getter's still-valid token.
  if (!getter) {
    cachedAgentAccessToken = null;
  }
}

// 60s clock-skew buffer: treat a token as expired slightly before its real `exp` so a request
// built right at the edge doesn't land server-side after expiry.
const TOKEN_EXPIRY_SKEW_SECONDS = 60;

// The single source request-time callers (the chat transport's headers builder, the prompt-mode
// client) resolve a token through — called on every request, including mid-run continuations and
// stream reconnects. Caches the last token + its decoded expiry so a still-valid cached token
// skips calling the Auth0 getter (which dispatches a context update that re-renders every
// useAuth0 consumer). A missing/failing/null getter falls back to the last cached token (even if
// expired — the request 401s through the normal error path), or null when there's never been one.
export async function getAgentAccessToken(): Promise<string | null> {
  const nowSeconds = Date.now() / 1000;
  if (cachedAgentAccessToken && nowSeconds < cachedAgentAccessToken.exp - TOKEN_EXPIRY_SKEW_SECONDS) {
    return cachedAgentAccessToken.token;
  }

  if (!agentAccessTokenGetter) {
    return cachedAgentAccessToken?.token ?? null;
  }

  try {
    const token = await agentAccessTokenGetter();
    if (!token) {
      return cachedAgentAccessToken?.token ?? null;
    }

    cachedAgentAccessToken = { token, exp: decodeJwtExpiry(token) };
    return token;
  } catch {
    return cachedAgentAccessToken?.token ?? null;
  }
}

function decodeJwtExpiry(token: string): number {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp ?? 0;
  } catch {
    return 0;
  }
}

export function getAgentSession(requestId: string): AgentSession {
  let session = agentSessions.get(requestId);
  if (!session) {
    session = createAgentSession(requestId);
    agentSessions.set(requestId, session);
  }

  return session;
}

export function clearAgentSessions(): void {
  agentSessions.clear();
  clearSubmittedFeedback();
}

// Resolves the session for the command's requestId and forwards the send only while at least
// one hook instance is currently mounted for it; otherwise it silently drops the command,
// preserving the old command bus's drop-when-no-hook-mounted semantic. (Unlike the bus, which
// fanned the send out once per mounted subscriber, this sends exactly once.)
export function emitSendMessage(command: SendMessageCommand): void {
  const session = agentSessions.get(command.requestId);
  if (!session?.isHookMounted()) return;

  void session.send(command.text, command.options);
}

function createAgentSession(requestId: string): AgentSession {
  const { resolveRuntime, resolveDocument, setRuntimeBinding } = createRuntimeBinding();

  // Mount tracking for emitSendMessage's drop-when-no-hook-mounted guard. A counter rather
  // than a boolean so overlapping hook instances for the same requestId (e.g. remount
  // windows) keep the session accepting commands until the last instance unmounts.
  let mountedHookCount = 0;

  // Prompt-request abort tracking + chat stop
  let activePromptAbortController: AbortController | null = null;
  let chat!: Chat<UIMessage>;
  const stopActiveRequest = () => {
    activePromptAbortController?.abort();
    chat.stop();
  };
  const startPromptRequest = (): AbortController => {
    const abortController = new AbortController();
    activePromptAbortController?.abort();
    activePromptAbortController = abortController;
    return abortController;
  };
  const finishPromptRequest = (abortController: AbortController) => {
    if (activePromptAbortController === abortController) {
      activePromptAbortController = null;
    }
  };

  const lifecycle: AgentRunLifecycle = createAgentRunLifecycle({
    requestId,
    resolveRuntime,
    resolveDocument,
    stop: stopActiveRequest,
    markEditorWritesRefused: (keptToolCallIds) => {
      chat.messages = markEditorWritesRefused(chat.messages, keptToolCallIds);
    },
  });

  const chatTranscript = createChatTranscript();
  // The per-run streaming flag is captured once here from the current runtime, so a mid-run URL
  // flip cannot change an in-flight run. Callers (send/retry/generateText) do not pass it.
  const beginAttempt = ({ runId, editTarget }: { runId: number; editTarget: AgentEditTarget }): PreparedAgentAttempt | null =>
    lifecycle.prepareAttempt(runId, editTarget, resolveRuntime()?.streamingEnabled ?? false);

  const getRetryEditTarget = (): AgentEditTarget => {
    const editTarget = getAgentRunEditTarget(requestId);
    if (!getTargetedEditRange(editTarget)) {
      return editTarget;
    }

    const editor = resolveRuntime()?.editor;
    if (!isAiToolkitReady(editor)) {
      return editTarget;
    }

    const mappedTargetRange = getAiToolkit(editor).getActiveSelection();
    return mappedTargetRange ? createTargetedEditTarget(mappedTargetRange) : editTarget;
  };

  // Chat callbacks
  const handleToolCall = async ({ toolCall }: { toolCall: { toolName: string; input: unknown; toolCallId: string } }) => {
    const runtime = resolveRuntime();
    if (!runtime) {
      handleError(new Error('Agent runtime unavailable for tool call'));
      return;
    }

    await handleAgentToolCall({
      requestId,
      runtime,
      toolCall,
      stop: stopActiveRequest,
    });
  };

  // Expected failures log at warn so `captureConsoleIntegration` does not turn them into Sentry
  // events: a credits refusal is an expected billing state, not a fault (`lifecycle.refuse` ends
  // that run quietly and hands the refused prompt back), an auth denial is handled at session level
  // (`fetchAgent` reports it and throws `AuthDeniedError`, so every denial reaches here typed;
  // `lifecycle.error` ends that run quietly), and network errors are already surfaced by the retry
  // UI (`lifecycle.error` classifies this same error with `isLikelyNetworkError`).
  const handleError = (error: Error) => {
    if (error instanceof AiCreditsExhaustedError) {
      console.warn('Agent error:', error);
      const refusedTurn = takeRefusedTurn(chat.messages);
      chat.messages = refusedTurn.messages;
      void lifecycle.refuse(refusedTurn.refusal);
      return;
    }

    if (error instanceof AuthDeniedError || isLikelyNetworkError(error)) {
      console.warn('Agent error:', error);
    } else {
      console.error('Agent error:', error);
    }
    void lifecycle.error(error, chat.messages);
  };

  // Cached chat transport
  const transport = new StreamingChatTransport({
    requestId,
    ...getAgentTransportConfig(getAgentAccessToken, ENVIRONMENT),
    fetch: fetchAgent,
    body: () => {
      const document = resolveDocument();
      return {
        ...buildMedicalClassificationRequestPayload({
          document,
          editor: resolveRuntime()?.editor ?? null,
        }),
        editScope: getAgentRunEditScope(requestId),
        ...getAgentRequestDocumentFields(document),
      };
    },
  });

  chat = new Chat<UIMessage>({
    id: requestId,
    transport,
    // A run whose terminalization is claimed (a cancel, an error, a size refusal decided inside
    // onFinish) sends nothing more.
    sendAutomaticallyWhen: ({ messages }) => shouldAutoSend({ messages }) && !hasAgentRunClaimedTerminalization(requestId),
    onToolCall: ({ toolCall }) => handleToolCall({ toolCall }),
    onFinish: (event) =>
      handleAgentFinish({
        event: event as AgentSessionFinishEvent,
        chat,
        chatTranscript,
        lifecycle,
        onError: handleError,
      }),
    onError: (error) => handleError(error),
  });

  const generateText = createGenerateTextAction({
    requestId,
    getAccessToken: getAgentAccessToken,
    resolveRuntime,
    beginAttempt,
    startPromptRequest,
    finishPromptRequest,
  });

  const session: AgentSession = {
    requestId,
    chat,
    // Public session API
    setRuntime(runtime) {
      setRuntimeBinding(runtime);
    },
    markMounted() {
      mountedHookCount += 1;
    },
    markUnmounted() {
      mountedHookCount -= 1;
    },
    isHookMounted() {
      return mountedHookCount > 0;
    },
    updateChatTranscript: chatTranscript.updateFromMessages,
    async send(text, options) {
      const runtime = resolveRuntime();
      const editTarget = options?.editTarget ?? createDocumentEditTarget();
      const runId = startAgentRun(requestId);
      const attempt = beginAttempt({ runId, editTarget });
      if (!attempt) return;

      let context: AgentContext | undefined;
      let requestOptions: ChatRequestOptions | undefined;
      const document = runtime?.document;
      if (runtime && document) {
        ({ context, requestOptions } = await resolveSendRequestContext({
          runtime,
          document,
          editTarget,
          toolkit: attempt.toolkit,
        }));
      }

      const toolName = getTargetedEditRange(editTarget) ? 'tiptapReadSelection' : 'tiptapRead';
      await sendWithPrefetch({ chat, currentEditor: attempt.editor, text, toolName, context, requestOptions });
    },
    generateText,
    generateDocumentName(variables) {
      const document = resolveDocument();
      return runPromptMode(
        'generate-document-name',
        { variables, ...getAgentRequestDocumentFields(document) },
        { getAccessToken: getAgentAccessToken }
      );
    },
    async retry() {
      const editTarget = getRetryEditTarget();
      const attempt = beginAttempt({
        runId: startAgentRun(requestId),
        editTarget,
      });
      if (!attempt) {
        return;
      }

      syncReplaySafeMessages(chat, chat.messages);
      const runtime = resolveRuntime();
      const document = runtime?.document;
      let requestOptions: ChatRequestOptions | undefined;
      if (runtime && document) {
        requestOptions = await resolveRetryRequestOptions(runtime, document);
      }
      await chat.regenerate(requestOptions);
    },
    async cancel() {
      await lifecycle.cancel(chat.messages);
    },
  };

  return session;
}

// Sessions outlive React mounts, so `document`/`persistDocumentPatch` keep sticky fallbacks to
// survive remount windows where the hook props are temporarily null (see docs/llm-agent/llm-agent-architecture.md invariant on
// finish-time document/review resolution tolerating null props during remount).
function createRuntimeBinding(): {
  resolveRuntime: () => AgentSessionRuntime | undefined;
  resolveDocument: () => PersistedDocument | undefined;
  setRuntimeBinding: (runtime: AgentSessionRuntime) => void;
} {
  const runtimeState: AgentSessionRuntimeState = {};

  const resolveRuntime = (): AgentSessionRuntime | undefined => {
    const runtime = runtimeState.runtime;
    if (!runtime) {
      return undefined;
    }

    return {
      ...runtime,
      document: runtime.document ?? runtimeState.documentFallback,
      persistDocumentPatch: runtime.persistDocumentPatch ?? runtimeState.persistDocumentPatchFallback,
    };
  };

  const resolveDocument = () => resolveRuntime()?.document;

  const setRuntimeBinding = (runtime: AgentSessionRuntime) => {
    if (runtime.document) {
      runtimeState.documentFallback = runtime.document;
    }
    if (runtime.persistDocumentPatch) {
      runtimeState.persistDocumentPatchFallback = runtime.persistDocumentPatch;
    }

    runtimeState.runtime = runtime;
  };

  return { resolveRuntime, resolveDocument, setRuntimeBinding };
}

// When the refused request was the turn's first, the model has seen nothing of the turn yet: only
// the user message and, for a prefetched send, its synthetic prefetch step follow the history. That
// turn is removed from the Chat so a later send does not replay it, and its text is handed back on
// the refusal. A refused continuation was sent automatically, not by the user, and the model already
// acted on the turn, so it keeps the history and carries no refusal.
function takeRefusedTurn(messages: UIMessage[]): { messages: UIMessage[]; refusal?: AiRefusal } {
  const userMessageIndex = messages.findLastIndex((message) => message.role === 'user');
  // No continuation exists without a prior user turn, so with no user message in the Chat the
  // refused request was started by the user; there is no prompt to hand back.
  if (userMessageIndex === -1) {
    return { messages, refusal: {} };
  }

  const followingMessages = messages.slice(userMessageIndex + 1);
  if (stripSyntheticPrefetchAssistantMessages(followingMessages).length > 0) return { messages };

  return { messages: messages.slice(0, userMessageIndex), refusal: { prompt: getDisplayText(messages[userMessageIndex]) } };
}

function syncReplaySafeMessages(chat: Chat<UIMessage>, messages: UIMessage[]): UIMessage[] {
  const normalizedMessages = normalizeReplayMessages(messages);
  if (normalizedMessages !== chat.messages) {
    chat.messages = normalizedMessages;
  }

  return normalizedMessages;
}

async function handleAgentFinish({
  event,
  chat,
  chatTranscript,
  lifecycle,
  onError,
}: {
  event: AgentSessionFinishEvent;
  chat: Chat<UIMessage>;
  chatTranscript: ReturnType<typeof createChatTranscript>;
  lifecycle: AgentRunLifecycle;
  onError: (error: Error) => void;
}): Promise<void> {
  const finalMessages = syncReplaySafeMessages(chat, getFinalMessages(chat, event));
  chatTranscript.updateFromMessages(finalMessages);
  if (event.isAbort || event.isError) return;

  try {
    if (shouldAutoSend({ messages: finalMessages })) {
      if (hasCompletedEditorWriteInCurrentTurn(finalMessages)) {
        await lifecycle.finalizeDocEdit(finalMessages);
      }
      return;
    }

    await lifecycle.finalizeRun(finalMessages);
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
  }
}

async function sendWithPrefetch({
  chat,
  currentEditor,
  text,
  toolName,
  context,
  requestOptions,
}: {
  chat: Chat<UIMessage>;
  currentEditor: Editor;
  text: string;
  toolName: 'tiptapRead' | 'tiptapReadSelection';
  context?: AgentContext;
  requestOptions?: ChatRequestOptions;
}): Promise<void> {
  const baseMessages = stripSyntheticPrefetchAssistantMessages(syncReplaySafeMessages(chat, chat.messages));
  const toolOutput = prefetchDocumentContent(currentEditor, toolName);

  if (toolOutput) {
    chat.messages = [...baseMessages, ...buildPrefetchMessages(text, toolName, toolOutput, context)];
    await chat.sendMessage(undefined, requestOptions);
    return;
  }

  if (baseMessages !== chat.messages) {
    chat.messages = baseMessages;
  }

  await chat.sendMessage({ text }, requestOptions);
}

async function resolveSendRequestContext({
  runtime,
  document,
  editTarget,
  toolkit,
}: {
  runtime: AgentSessionRuntime;
  document: PersistedDocument;
  editTarget: AgentEditTarget;
  toolkit: PreparedAgentAttempt['toolkit'];
}): Promise<{ context: AgentContext; requestOptions: ChatRequestOptions | undefined }> {
  const llmContext = await runtime.getContext(getContextParams(document));
  const schemaAwareness = toolkit.getHtmlSchemaAwareness();

  return {
    context: buildAgentDocumentContext(llmContext, schemaAwareness, getAgentEditScope(editTarget)),
    requestOptions: buildChatRequestOptions({
      userSources: serializeUserSources(llmContext.userSources),
      ...buildAgentInstructionBodyFields(llmContext),
    }),
  };
}

async function resolveRetryRequestOptions(
  runtime: AgentSessionRuntime,
  document: PersistedDocument
): Promise<ChatRequestOptions | undefined> {
  const llmContext = await runtime.getContext(getContextParams(document));
  return buildChatRequestOptions({
    userSources: serializeUserSources(llmContext.userSources),
    ...buildAgentInstructionBodyFields(llmContext),
  });
}

function normalizeReplayMessages(messages: UIMessage[]): UIMessage[] {
  let changed = false;

  const normalizedMessages = messages.map((message) => {
    if (message.role !== 'assistant' || !Array.isArray(message.parts)) {
      return message;
    }

    let messageChanged = false;
    const normalizedParts = message.parts.map((part: any) => {
      if (typeof part?.type !== 'string' || !part.type.startsWith('tool-')) {
        return part;
      }

      const normalizedInput = normalizeReplayToolInput(part.input);
      if (normalizedInput === part.input) {
        return part;
      }

      messageChanged = true;
      return {
        ...part,
        input: normalizedInput,
      };
    });

    if (!messageChanged) {
      return message;
    }

    changed = true;
    return {
      ...message,
      parts: normalizedParts,
    };
  });

  return changed ? normalizedMessages : messages;
}

function normalizeReplayToolInput(input: unknown): unknown {
  if (typeof input !== 'string') return input;

  const trimmed = input.trim();
  if (!trimmed.startsWith('{')) return input;

  try {
    const parsed = JSON.parse(trimmed);
    return isReplayToolInputObject(parsed) ? parsed : input;
  } catch {
    return input;
  }
}

function isReplayToolInputObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getFinalMessages(chat: Chat<UIMessage>, event: AgentSessionReplayFinishEvent): UIMessage[] {
  return Array.isArray(event.messages) ? event.messages : chat.messages;
}

function buildMedicalClassificationRequestPayload({
  document,
  editor,
}: {
  document?: PersistedDocument;
  editor?: Editor | null;
}): {
  parsedContent?: string;
  classificationCache?: ClassificationCache;
} {
  if (!document || !isNoteDocument(document)) return {};

  const parsedContent = getCurrentNoteParsedContent(editor) ?? normalizeClassificationSource(document.parsedContent ?? '');
  const rawCache = document.classificationCache as Partial<ClassificationCache> | null | undefined;
  const classificationCache =
    rawCache?.classification && typeof rawCache.contentHash === 'string'
      ? {
          contentHash: rawCache.contentHash,
          classification: rawCache.classification,
        }
      : undefined;

  return classificationCache ? { parsedContent, classificationCache } : { parsedContent };
}

function getCurrentNoteParsedContent(editor?: Editor | null): string | null {
  if (!isEditorAlive(editor) || typeof editor.getHTML !== 'function') return null;

  return normalizeClassificationSource(stripHtml(getEditorHTML(editor)));
}
